/**
 * Shared help pieces for review, strategy, setup, users, settings and onboarding.
 * The icon itself is the one app-wide HelpTip (components/ui/help-tip.tsx); texts live in src/help/helpText.ts.
 */
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../lib/utils.js';
import { HelpTip } from '../../components/ui/help-tip.js';
import type { HelpKey } from '../../help/helpText.js';
export { HELP, type HelpEntry, type HelpKey } from '../../help/helpText.js';
export { HelpTip };

/** Title text with its help icon. */
export function Titled({ children, k }: { children: ReactNode; k: HelpKey }) {
  return <span className="inline-flex items-center gap-1.5">{children}<HelpTip k={k} /></span>;
}

/** FormField with a help icon on the label (FormField's label is text-only). Wires id / aria-describedby. */
export function Field({ label, help, hint, error, className, children }: { label: string; help: HelpKey; hint?: ReactNode; error?: string | null; className?: string; children: ReactElement }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  const child = isValidElement(children) ? cloneElement(children as ReactElement<Record<string, unknown>>, { id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined }) : children;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium">{label}</label>
        <HelpTip k={help} />
      </div>
      {child}
      {hint && !error && <p id={hintId} className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p id={errId} className="text-xs font-medium text-danger">{error}</p>}
    </div>
  );
}
